import { Link as RouterLink, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardHeader,
  Title3,
  Body1,
  Caption1,
  Button,
  tokens,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';
import { ArrowLeft20Regular, DocumentArrowDown20Regular, ArrowDownload20Regular } from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { FamilyBadge, SeverityBadge, StatusBadge } from '../components/StatusBadge';
import { useScanById } from '../hooks/useScans';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { computeScoreBreakdown } from '../domain/scanEngine';
import { controlsById, familyLabels } from '../domain/controls';
import { statusColors } from '../theme/brandTheme';
import { getResultMessage } from '../domain/resultMessage';

export function ScanDetailPage() {
  const { t, i18n }    = useTranslation();
  const { scanId }     = useParams<{ scanId: string }>();
  // Archi B : useScanById fait un GET /api/scans/{scanId} via TanStack Query.
  // Plus de localStorage scanStore (qui ne contient que les scans demo).
  const scan           = useScanById(scanId);
  const { tenant }     = useActiveTenant();

  if (!scan) {
    return (
      <div>
        <PageHeader title={t('scans.detail.notFound')} />
        <Body1>{t('scans.detail.notFoundBody', { id: scanId })}</Body1>
        <div style={{ marginTop: 16 }}>
          <RouterLink to="/scans" style={{ textDecoration: 'none' }}>
            <Button appearance="primary" icon={<ArrowLeft20Regular />}>{t('scans.detail.back')}</Button>
          </RouterLink>
        </div>
      </div>
    );
  }

  const score = computeScoreBreakdown(scan);
  // Backend a calcule globalScore ? on override pour eviter divergence d arrondi
  if (typeof scan.globalScore === 'number') {
    score.globalScore = scan.globalScore;
  }

  // Export CSV des resultats du scan (cote client, sans backend).
  // Separateur ';' + BOM UTF-8 pour un rendu correct dans Excel (locale FR).
  function handleDownloadCsv() {
    if (!scan) return;
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = [
      t('controls.list.id'),
      t('controls.table.family'),
      t('controls.list.control'),
      t('controls.table.severity'),
      t('controls.filter.status'),
      t('scans.detail.evidence'),
    ];
    const lines = scan.results.map((r) => {
      const def = controlsById[r.controlId];
      return [
        def?.id ?? r.controlId,
        def ? t(`family.${def.family}`, { defaultValue: familyLabels[def.family] }) : '',
        def?.title ?? '',
        def?.severity ?? '',
        r.status,
        getResultMessage(r, t),
      ];
    });
    const csv = [header, ...lines].map((cols) => cols.map(esc).join(';')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scan_${scan.id}_${new Date(scan.startedAt).toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <PageHeader
        title={t('scans.detail.title', { id: scan.id })}
        description={`${t('scans.detail.metaStarted')} : ${new Date(scan.startedAt).toLocaleString(i18n.language)} — ${t('scans.detail.metaTriggered')} : ${scan.meta.triggeredBy}`}
        actions={
          <>
            <RouterLink to="/scans" style={{ textDecoration: 'none' }}>
              <Button appearance="subtle" icon={<ArrowLeft20Regular />}>{t('scans.detail.back')}</Button>
            </RouterLink>
            <Button appearance="subtle" icon={<ArrowDownload20Regular />} onClick={handleDownloadCsv}>
              {t('scans.detail.downloadCsv', 'Télécharger le résultat (CSV)')}
            </Button>
            <RouterLink to="/reports" style={{ textDecoration: 'none' }}>
              <Button appearance="primary" icon={<DocumentArrowDown20Regular />}>
                {t('dashboard.actions.generateReport')}
              </Button>
            </RouterLink>
          </>
        }
      />

      {/* Synthese - 6 cards : score + 5 statuts */}
      <div
        style={{
          display:              'grid',
          gridTemplateColumns:  'repeat(auto-fit, minmax(140px, 1fr))',
          gap:                  16,
          marginBottom:         16,
        }}
      >
        <SummaryCard label={t('dashboard.globalScore')}        value={`${score.globalScore} / 100`} color={scoreColor(score.globalScore)} />
        <SummaryCard label={t('dashboard.stats.compliant')}    value={`${score.counts.pass}`}    color={statusColors.pass} />
        <SummaryCard label={t('dashboard.stats.improve')}      value={`${score.counts.warning}`} color={statusColors.warning} />
        <SummaryCard label={t('dashboard.stats.nonCompliant')} value={`${score.counts.fail}`}    color={statusColors.fail} />
        <SummaryCard label={t('dashboard.stats.manual')}       value={`${score.counts.manual}`}  color={statusColors.manual} />
        <SummaryCard label={t('dashboard.stats.error')}        value={`${score.counts.error}`}   color={statusColors.error} />
      </div>

      {/* Tableau des resultats */}
      <Card>
        <CardHeader header={<Title3>{t('scans.detail.controls')}</Title3>} />
        <Table arial-label={t('scans.detail.controls')}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{t('controls.list.id')}</TableHeaderCell>
              <TableHeaderCell>{t('controls.table.family')}</TableHeaderCell>
              <TableHeaderCell>{t('controls.list.control')}</TableHeaderCell>
              <TableHeaderCell>{t('controls.table.severity')}</TableHeaderCell>
              <TableHeaderCell>{t('controls.filter.status')}</TableHeaderCell>
              <TableHeaderCell>{t('scans.detail.evidence')}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {scan.results.map((r) => {
              const def = controlsById[r.controlId];
              if (!def) return null;
              const verdict = getResultMessage(r, t);
              return (
                <TableRow key={r.controlId}>
                  <TableCell>
                    <RouterLink
                      to={`/controls/${def.id}`}
                      style={{ color: tokens.colorBrandForeground1, fontWeight: 600, textDecoration: 'none' }}
                    >
                      {def.id}
                    </RouterLink>
                  </TableCell>
                  <TableCell><FamilyBadge family={def.family} /></TableCell>
                  <TableCell>
                    <Body1 style={{ fontWeight: 600 }}>{def.title}</Body1>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                      {t(`family.${def.family}`, { defaultValue: familyLabels[def.family] })}
                    </Caption1>
                  </TableCell>
                  <TableCell><SeverityBadge severity={def.severity} /></TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell>
                    <Caption1 style={{ color: tokens.colorNeutralForeground2 }}>{verdict}</Caption1>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Caption1 style={{ display: 'block', marginTop: 12, color: tokens.colorNeutralForeground3, fontSize: 11 }}>
        {t('scans.detail.metaTenant')} : <strong>{tenant?.name ?? scan.tenantId}</strong>
        {' · '}
        {t('scans.detail.metaCatalog')} : {scan.meta.catalogVersion}
      </Caption1>
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <Card style={{ borderLeft: `4px solid ${color}` }}>
      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{label}</Caption1>
      <Body1 style={{ display: 'block', fontSize: 24, fontWeight: 600, marginTop: 4, color }}>
        {value}
      </Body1>
    </Card>
  );
}

function scoreColor(score: number): string {
  if (score >= 80) return statusColors.pass;
  if (score >= 60) return statusColors.warning;
  return statusColors.fail;
}
